import React, { useEffect, useState } from "react";
import {
  Check,
  X,
  RefreshCw,
  Eye,
  MapPin,
  User,
  Calendar,
  Server,
  Tag,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  Maximize,
} from "lucide-react";

import { toast } from "sonner";
import { api } from "@/lib/api";
import { createWorker } from "tesseract.js";

const STORAGE = "lostdc:admin";

const getErrorMessage = (error, fallback) => {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
};

export default function AdminBuildModeration() {
  const [builds, setBuilds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [selectedBuild, setSelectedBuild] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [fullscreenImage, setFullscreenImage] = useState(false);
  const [rejectingBuild, setRejectingBuild] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [autoChecking, setAutoChecking] = useState(false);
  const [autoResults, setAutoResults] = useState({});
  const [ocrReady, setOcrReady] = useState(false);

  const headers = {
    "X-Admin-Token": localStorage.getItem(STORAGE) || "",
  };

  const loadBuilds = async () => {
    setLoading(true);

    try {
      const { data } = await api.get("/builds?pending=true", {
        headers,
      });

      setBuilds(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load pending builds:", err);
      toast.error(getErrorMessage(err, "Failed to load pending builds"));
      setBuilds([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBuilds();
  }, []);

  // Automatically verify every pending build after the queue loads.
  // Only matching screenshots are auto-approved; everything else
  // remains in the manual moderation queue.
  useEffect(() => {
    if (loading || !builds.length) return;

    let cancelled = false;

    const runAutomaticModeration = async () => {
      for (const build of builds) {
        if (cancelled) return;

        // Don't repeatedly process a build already checked in this session.
        if (autoResults[build.id]) continue;

        const result = await autoVerifyBuild(build);

        if (cancelled) return;

        if (result.approved) {
          setActionId(build.id);

          try {
            await api.post(
              `/builds/${build.id}/approve`,
              {
                approved: true,
                reason:
                  "Auto-approved: LosTDC SURVIVAL scoreboard detected in yellow on the right side of the submitted screenshot.",
              },
              { headers }
            );

            toast.success(
              `"${build.title || "Build"}" auto-approved — LosTDC SURVIVAL verified`
            );

            setBuilds((prev) =>
              prev.filter((item) => item.id !== build.id)
            );

            if (selectedBuild?.id === build.id) {
              setSelectedBuild(null);
            }
          } catch (err) {
            console.error("Automatic approval error:", err);
            toast.error(
              getErrorMessage(err, "Automatic approval failed")
            );
          } finally {
            setActionId(null);
          }
        }
      }
    };

    runAutomaticModeration();

    return () => {
      cancelled = true;
    };
  }, [loading, builds.length]);


  const openBuildViewer = (build) => {
    setSelectedBuild(build);
    setSelectedImageIndex(0);
    setFullscreenImage(false);
  };

  const closeBuildViewer = () => {
    setSelectedBuild(null);
    setSelectedImageIndex(0);
    setFullscreenImage(false);
  };

  const moveImage = (direction) => {
    if (!selectedBuild) return;

    const images = getImages(selectedBuild);

    if (images.length <= 1) return;

    setSelectedImageIndex((current) => {
      if (direction === "next") {
        return (current + 1) % images.length;
      }

      return (current - 1 + images.length) % images.length;
    });
  };

  // ------------------------------------------------------------
  // Automatic screenshot verification
  // Looks at the right-side scoreboard, OCRs it, and verifies:
  //   1. "LosTDC SURVIVAL" is present
  //   2. The detected text is yellow/gold
  //
  // This is intentionally conservative: if OCR/color verification
  // is uncertain, the build stays pending for manual review.
  // ------------------------------------------------------------

  // The supplied Minecraft screenshot shows the right-side scoreboard
  // around the upper-right portion of the image. We therefore use a
  // normalized crop instead of OCR-ing the entire right side.
  const SCOREBOARD_X_START = 0.78;
  const SCOREBOARD_Y_START = 0.20;
  const SCOREBOARD_Y_END = 0.58;

  const normalizeOcrText = (value) =>
    String(value || "")
      .replace(/[|]/g, "I")
      .replace(/0/g, "O")
      .replace(/[’'`]/g, "")
      .replace(/[^A-Z0-9]+/gi, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toUpperCase();

  const compactOcrText = (value) =>
    normalizeOcrText(value).replace(/[^A-Z0-9]/g, "");

  const isYellowPixel = (r, g, b) => {
    // Tuned for the yellow/gold Minecraft scoreboard text in the
    // supplied screenshot. We intentionally allow a fairly broad
    // range because screenshots can have different brightness.
    if (r < 130 || g < 90) return false;

    return (
      r > b * 1.45 &&
      g > b * 1.25 &&
      r >= g * 0.95 &&
      r - b >= 55
    );
  };

  const loadImageForCanvas = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Unable to load screenshot"));

      img.src = src;
    });

  const getScoreboardCanvases = async (imageUrl) => {
    const img = await loadImageForCanvas(imageUrl);

    const sourceWidth = img.naturalWidth || img.width;
    const sourceHeight = img.naturalHeight || img.height;

    if (!sourceWidth || !sourceHeight) {
      throw new Error("Invalid screenshot dimensions");
    }

    const cropX = Math.floor(sourceWidth * SCOREBOARD_X_START);
    const cropY = Math.floor(sourceHeight * SCOREBOARD_Y_START);
    const cropWidth = sourceWidth - cropX;
    const cropHeight = Math.floor(
      sourceHeight * (SCOREBOARD_Y_END - SCOREBOARD_Y_START)
    );

    // Original-color crop for visual debugging.
    const debugCanvas = document.createElement("canvas");
    debugCanvas.width = cropWidth;
    debugCanvas.height = cropHeight;

    const debugCtx = debugCanvas.getContext("2d", {
      willReadFrequently: true,
    });

    if (!debugCtx) throw new Error("Canvas is not available");

    debugCtx.drawImage(
      img,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    );

    // Upscale 3x for OCR. Minecraft's scoreboard font is small,
    // so giving Tesseract larger glyphs substantially improves OCR.
    const scale = 3;
    const ocrCanvas = document.createElement("canvas");
    ocrCanvas.width = cropWidth * scale;
    ocrCanvas.height = cropHeight * scale;

    const ocrCtx = ocrCanvas.getContext("2d", {
      willReadFrequently: true,
    });

    if (!ocrCtx) throw new Error("OCR canvas is not available");

    ocrCtx.imageSmoothingEnabled = false;
    ocrCtx.drawImage(
      debugCanvas,
      0,
      0,
      cropWidth,
      cropHeight,
      0,
      0,
      ocrCanvas.width,
      ocrCanvas.height
    );

    return {
      debugCanvas,
      ocrCanvas,
      sourceWidth,
      sourceHeight,
      crop: {
        x: cropX,
        y: cropY,
        width: cropWidth,
        height: cropHeight,
      },
    };
  };

  const createYellowMaskCanvas = (sourceCanvas) => {
    const width = sourceCanvas.width;
    const height = sourceCanvas.height;

    const sourceCtx = sourceCanvas.getContext("2d", {
      willReadFrequently: true,
    });

    if (!sourceCtx) throw new Error("Source canvas unavailable");

    const source = sourceCtx.getImageData(0, 0, width, height);
    const output = new ImageData(width, height);

    for (let i = 0; i < source.data.length; i += 4) {
      const r = source.data[i];
      const g = source.data[i + 1];
      const b = source.data[i + 2];

      if (isYellowPixel(r, g, b)) {
        // White text on black background makes OCR easier.
        output.data[i] = 255;
        output.data[i + 1] = 255;
        output.data[i + 2] = 255;
        output.data[i + 3] = 255;
      } else {
        output.data[i] = 0;
        output.data[i + 1] = 0;
        output.data[i + 2] = 0;
        output.data[i + 3] = 255;
      }
    }

    const maskCanvas = document.createElement("canvas");
    maskCanvas.width = width;
    maskCanvas.height = height;

    const maskCtx = maskCanvas.getContext("2d", {
      willReadFrequently: true,
    });

    if (!maskCtx) throw new Error("Mask canvas unavailable");

    maskCtx.putImageData(output, 0, 0);

    return maskCanvas;
  };

  const calculateYellowCoverage = (canvas) => {
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return 0;

    const { width, height } = canvas;
    const pixels = ctx.getImageData(0, 0, width, height).data;

    let totalNonDark = 0;
    let yellowPixels = 0;

    for (let y = 0; y < height; y += 2) {
      for (let x = 0; x < width; x += 2) {
        const i = (y * width + x) * 4;
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];

        if (r + g + b < 90) continue;

        totalNonDark++;

        if (isYellowPixel(r, g, b)) {
          yellowPixels++;
        }
      }
    }

    return totalNonDark ? yellowPixels / totalNonDark : 0;
  };

  // OCR on Minecraft's pixel font can turn:
  //   LOSTDC SURVIVAL
  // into things such as:
  //   LOATDC SEWIVAL
  // We use a small Levenshtein distance instead of requiring an
  // exact OCR spelling.
  const levenshteinDistance = (a, b) => {
    const aa = String(a || "");
    const bb = String(b || "");

    const previous = Array.from({ length: bb.length + 1 }, (_, i) => i);

    for (let i = 1; i <= aa.length; i++) {
      const current = [i];

      for (let j = 1; j <= bb.length; j++) {
        const insertion = current[j - 1] + 1;
        const deletion = previous[j] + 1;
        const substitution =
          previous[j - 1] + (aa[i - 1] === bb[j - 1] ? 0 : 1);

        current.push(Math.min(insertion, deletion, substitution));
      }

      for (let j = 0; j < current.length; j++) {
        previous[j] = current[j];
      }
    }

    return previous[bb.length];
  };

  const fuzzyWordMatch = (word, target, maxDistance) => {
    const clean = compactOcrText(word);

    if (!clean) return false;
    if (clean === target) return true;

    return levenshteinDistance(clean, target) <= maxDistance;
  };

  const fuzzyContainsLosTdcSurvival = (text) => {
    const normalized = normalizeOcrText(text);

    if (!normalized) return false;

    // Exact match first.
    if (compactOcrText(normalized).includes("LOSTDCSURVIVAL")) {
      return true;
    }

    const words = normalized.split(/\s+/).filter(Boolean);

    // Check individual OCR tokens.
    const lostTdcCandidates = words.filter((word) =>
      fuzzyWordMatch(word, "LOSTDC", 2)
    );

    const survivalCandidates = words.filter((word) =>
      fuzzyWordMatch(word, "SURVIVAL", 3)
    );

    if (lostTdcCandidates.length && survivalCandidates.length) {
      return true;
    }

    // Also check adjacent tokens because OCR may split LOSTDC.
    for (let i = 0; i < words.length; i++) {
      const joined = `${words[i]}${words[i + 1] || ""}`;

      if (
        fuzzyWordMatch(joined, "LOSTDC", 2) &&
        words
          .slice(i + 2, i + 4)
          .some((word) => fuzzyWordMatch(word, "SURVIVAL", 3))
      ) {
        return true;
      }
    }

    // Finally, check a compact phrase window. This catches OCR such as:
    // "LOATDC SEWIVAL"
    const compact = compactOcrText(normalized);

    for (let i = 0; i < compact.length; i++) {
      const window = compact.slice(i, i + "LOSTDCSURVIVAL".length + 3);

      if (levenshteinDistance(window, "LOSTDCSURVIVAL") <= 5) {
        return true;
      }
    }

    return false;
  };

  const verifyScreenshot = async (imageUrl, worker) => {
    const {
      debugCanvas,
      ocrCanvas,
      crop,
      sourceWidth,
      sourceHeight,
    } = await getScoreboardCanvases(imageUrl);

    const yellowCoverage = calculateYellowCoverage(debugCanvas);

    // OCR both the normal enlarged crop and a yellow-only mask.
    const normalResult = await worker.recognize(ocrCanvas);
    const yellowMask = createYellowMaskCanvas(ocrCanvas);
    const yellowResult = await worker.recognize(yellowMask);

    const normalText = normalizeOcrText(normalResult?.data?.text);
    const yellowText = normalizeOcrText(yellowResult?.data?.text);

    const detectedText = [normalText, yellowText]
      .filter(Boolean)
      .join(" | ");

    const textMatch =
      fuzzyContainsLosTdcSurvival(normalText) ||
      fuzzyContainsLosTdcSurvival(yellowText);

    // We don't require a huge yellow percentage. The scoreboard title
    // is only a small part of the crop, so a modest yellow signal is
    // enough when OCR also identifies the required text.
    const yellowMatch = yellowCoverage >= 0.008;

    const approved = textMatch && yellowMatch;

    return {
      approved,
      reason: approved
        ? "LosTDC SURVIVAL detected in the yellow right-side scoreboard."
        : !textMatch
          ? "OCR could not confirm LosTDC SURVIVAL in the right scoreboard crop."
          : "LosTDC SURVIVAL was read, but yellow scoreboard color was not confirmed.",
      text: detectedText,
      normalText,
      yellowText,
      yellowCoverage,
      crop,
      sourceWidth,
      sourceHeight,
      debugImage: debugCanvas.toDataURL("image/png"),
    };
  };

  const createOcrWorker = async () => {
    const worker = await createWorker("eng");
    return worker;
  };

  const autoVerifyBuild = async (build) => {
    const images = getImages(build);

    if (!images.length) {
      return {
        approved: false,
        reason: "No screenshots uploaded.",
        checkedImages: 0,
      };
    }

    setAutoChecking(true);

    let worker;

    try {
      worker = await createOcrWorker();
      setOcrReady(true);

      const results = [];

      for (const image of images) {
        try {
          results.push(await verifyScreenshot(image, worker));
        } catch (error) {
          console.error("Screenshot verification failed:", error);

          results.push({
            approved: false,
            reason: "Could not read this screenshot.",
            text: "",
            yellowCoverage: 0,
          });
        }
      }

      // Require at least one valid screenshot to contain the exact
      // scoreboard evidence. If multiple screenshots are uploaded,
      // any matching screenshot is enough to prove the scoreboard.
      const match = results.find((result) => result.approved);

      const finalResult = match || {
        approved: false,
        reason: results
          .map((result) => result.reason)
          .filter(Boolean)
          .join(" "),
        text: results.map((result) => result.text).filter(Boolean).join(" | "),
        yellowCoverage: Math.max(
          0,
          ...results.map((result) => result.yellowCoverage || 0)
        ),
      };

      setAutoResults((prev) => ({
        ...prev,
        [build.id]: {
          ...finalResult,
          checkedImages: images.length,
        },
      }));

      return finalResult;
    } finally {
      if (worker) {
        await worker.terminate();
      }

      setAutoChecking(false);
    }
  };

  const autoApproveBuild = async (build) => {
    const result = await autoVerifyBuild(build);

    if (!result.approved) {
      toast.error(`Manual review required: ${result.reason}`);
      return;
    }

    setActionId(build.id);

    try {
      await api.post(
        `/builds/${build.id}/approve`,
        {
          approved: true,
          reason: "Auto-approved: LosTDC SURVIVAL scoreboard detected in yellow on the right side of the submitted screenshot.",
        },
        { headers }
      );

      toast.success("Build auto-approved — LosTDC SURVIVAL verified");

      setBuilds((prev) => prev.filter((item) => item.id !== build.id));

      if (selectedBuild?.id === build.id) {
        setSelectedBuild(null);
      }
    } catch (err) {
      console.error("Auto-approve build error:", err);
      toast.error(getErrorMessage(err, "Failed to auto-approve build"));
    } finally {
      setActionId(null);
    }
  };

  const approveBuild = async (build) => {
    if (!window.confirm(`Approve "${build.title}"?`)) return;

    setActionId(build.id);

    try {
      await api.post(
        `/builds/${build.id}/approve`,
        {
          approved: true,
          reason: "Approved by admin",
        },
        { headers }
      );

      toast.success("Build approved");
      setBuilds((prev) => prev.filter((item) => item.id !== build.id));

      if (selectedBuild?.id === build.id) {
        setSelectedBuild(null);
      }
    } catch (err) {
      console.error("Approve build error:", err);
      toast.error(getErrorMessage(err, "Failed to approve build"));
    } finally {
      setActionId(null);
    }
  };

  const openReject = (build) => {
    setRejectingBuild(build);
    setRejectReason("");
  };

  const rejectBuild = async () => {
    if (!rejectingBuild) return;

    const reason = rejectReason.trim();

    if (!reason) {
      toast.error("Please enter a rejection reason");
      return;
    }

    const build = rejectingBuild;

    setActionId(build.id);

    try {
      await api.post(
        `/builds/${build.id}/approve`,
        {
          approved: false,
          reason,
        },
        { headers }
      );

      toast.success("Build rejected");

      setBuilds((prev) => prev.filter((item) => item.id !== build.id));
      setRejectingBuild(null);
      setRejectReason("");

      if (selectedBuild?.id === build.id) {
        setSelectedBuild(null);
      }
    } catch (err) {
      console.error("Reject build error:", err);
      toast.error(getErrorMessage(err, "Failed to reject build"));
    } finally {
      setActionId(null);
    }
  };

  const formatDate = (value) => {
    if (!value) return "Unknown";

    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  const getImages = (build) => {
    if (Array.isArray(build?.images) && build.images.length) {
      return build.images;
    }

    if (build?.image) {
      return [build.image];
    }

    return [];
  };

  return (
    <div className="space-y-6" data-testid="admin-build-moderation">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">
            Community Builds
          </div>

          <h2 className="font-hero text-2xl mt-1">
            Build Approval Queue
          </h2>

          <p className="text-sm text-white/50 mt-1">
            Review player submissions before they become public.
          </p>
        </div>

        <button
          onClick={loadBuilds}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:bg-white/5 text-sm disabled:opacity-50"
        >
          <RefreshCw
            size={14}
            className={loading ? "animate-spin" : ""}
          />
          Refresh
        </button>
      </div>

      {/* Counter */}
      <div className="rounded-2xl glass p-5">
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">
          Pending submissions
        </div>

        <div className="font-hero text-4xl mt-1">
          {builds.length}
        </div>
      </div>

      {/* Automatic verification status */}
      <div className="rounded-2xl glass p-5 border border-yellow-400/10">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-yellow-300/70">
              Automatic Screenshot Verification
            </div>
            <div className="text-sm text-white/60 mt-1">
              Reads the right-side scoreboard and looks for{" "}
              <span className="text-yellow-300 font-semibold">
                LosTDC SURVIVAL
              </span>{" "}
              in yellow.
            </div>
          </div>

          <div className="shrink-0 text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
            {autoChecking ? "Checking..." : ocrReady ? "Ready" : "Idle"}
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="rounded-2xl glass p-8 text-center text-white/50">
          <RefreshCw size={22} className="animate-spin mx-auto mb-3" />
          Loading pending builds...
        </div>
      )}

      {/* Empty */}
      {!loading && builds.length === 0 && (
        <div className="rounded-2xl glass p-10 text-center">
          <Check size={32} className="mx-auto mb-3 opacity-50" />

          <div className="font-semibold">
            No pending builds
          </div>

          <div className="text-sm text-white/40 mt-1">
            All submitted builds have been reviewed.
          </div>
        </div>
      )}

      {/* Build queue */}
      {!loading && builds.length > 0 && (
        <div className="grid gap-4">
          {builds.map((build) => {
            const images = getImages(build);
            const busy = actionId === build.id;

            return (
              <div
                key={build.id}
                className="rounded-2xl glass overflow-hidden"
                data-testid={`pending-build-${build.id}`}
              >
                <div className="flex flex-col lg:flex-row">
                  {/* Preview */}
                  <div className="lg:w-[420px] xl:w-[480px] h-64 lg:h-[300px] xl:h-[340px] bg-black/40 shrink-0">
                    {images[0] ? (
                      <img
                        src={images[0]}
                        alt={build.title || "Build"}
                        className="w-full h-full object-cover cursor-pointer hover:scale-[1.02] transition-transform"
                        onClick={() => openBuildViewer(build)}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/30">
                        <ImageIcon size={36} />
                      </div>
                    )}
                  </div>

                  {/* Details */}
                  <div className="p-5 flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-hero text-xl truncate">
                          {build.title || "Untitled Build"}
                        </h3>

                        <div className="text-xs text-white/40 mt-1">
                          ID: {build.id}
                        </div>
                      </div>

                      <span className="shrink-0 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest bg-yellow-500/10 text-yellow-300 border border-yellow-400/20">
                        Pending
                      </span>
                    </div>

                    {build.description && (
                      <p className="text-sm text-white/60 mt-3 line-clamp-3">
                        {build.description}
                      </p>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4 text-xs text-white/60">
                      <div className="flex items-center gap-2">
                        <User size={13} />
                        <span>
                          {build.author_name || build.author || "Unknown"}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Server size={13} />
                        <span>{build.server || "Unknown server"}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Tag size={13} />
                        <span>{build.category || "Spawn"}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Calendar size={13} />
                        <span>{formatDate(build.created_at)}</span>
                      </div>

                      {build.coordinates && (
                        <div className="flex items-center gap-2 sm:col-span-2">
                          <MapPin size={13} />
                          <span>{build.coordinates}</span>
                        </div>
                      )}
                    </div>

                    {/* Tags */}
                    {Array.isArray(build.tags) && build.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-4">
                        {build.tags.map((tag, index) => (
                          <span
                            key={`${tag}-${index}`}
                            className="px-2 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] text-white/50"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Automatic verification result */}
                    {autoResults[build.id] && (
                      <div
                        className={`mb-3 rounded-xl border px-3 py-3 text-xs ${
                          autoResults[build.id].approved
                            ? "border-green-400/20 bg-green-500/10 text-green-300"
                            : "border-yellow-400/20 bg-yellow-500/5 text-yellow-200"
                        }`}
                      >
                        <div className="font-semibold">
                          {autoResults[build.id].approved
                            ? "✓ Auto verification passed"
                            : "⚠ Manual review required"}
                        </div>

                        <div className="text-white/50 mt-1">
                          {autoResults[build.id].reason}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 text-[11px]">
                          <div className="rounded-lg bg-black/20 px-2 py-1.5">
                            <span className="text-white/30">OCR:</span>{" "}
                            <span className="text-white/70">
                              {autoResults[build.id].text || "Nothing detected"}
                            </span>
                          </div>

                          <div className="rounded-lg bg-black/20 px-2 py-1.5">
                            <span className="text-white/30">Yellow:</span>{" "}
                            <span className="text-white/70">
                              {(
                                (autoResults[build.id].yellowCoverage || 0) *
                                100
                              ).toFixed(1)}
                              %
                            </span>
                          </div>
                        </div>

                        {autoResults[build.id].debugImage && (
                          <details className="mt-2">
                            <summary className="cursor-pointer text-white/40 hover:text-white/70">
                              Show detected scoreboard area
                            </summary>
                            <img
                              src={autoResults[build.id].debugImage}
                              alt="OCR scoreboard crop"
                              className="mt-2 w-full max-w-sm rounded-lg border border-white/10 bg-black object-contain"
                            />
                          </details>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 mt-5">
                      <button
                        onClick={() => openBuildViewer(build)}
                        disabled={busy}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:bg-white/5 text-xs disabled:opacity-50"
                      >
                        <Eye size={14} />
                        View
                      </button>

                      <button
                        onClick={() => autoApproveBuild(build)}
                        disabled={busy || autoChecking}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-yellow-400/30 text-yellow-200 text-xs font-semibold hover:bg-yellow-500/10 disabled:opacity-50"
                      >
                        <Eye size={14} />
                        {autoChecking ? "Checking..." : "Auto Check"}
                      </button>

                      <button
                        onClick={() => approveBuild(build)}
                        disabled={busy || autoChecking}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFBF00] text-black text-xs font-semibold hover:bg-[#FF8C00] disabled:opacity-50"
                      >
                        <Check size={14} />
                        {busy ? "Processing..." : "Approve"}
                      </button>

                      <button
                        onClick={() => openReject(build)}
                        disabled={busy}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-red-400/30 text-red-300 hover:bg-red-500/10 text-xs disabled:opacity-50"
                      >
                        <X size={14} />
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* View modal */}
      {selectedBuild && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md p-2 sm:p-4 overflow-y-auto"
          onClick={closeBuildViewer}
        >
          <div
            className="w-full max-w-7xl mx-auto mt-2 sm:mt-6 rounded-2xl border border-white/10 bg-[#090909] shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-4 px-4 sm:px-6 py-4 border-b border-white/10">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">
                  Community Build Review
                </div>

                <h2 className="font-hero text-xl sm:text-3xl mt-1 truncate">
                  {selectedBuild.title || "Untitled Build"}
                </h2>

                <div className="text-xs text-white/40 mt-1">
                  {getImages(selectedBuild).length > 0
                    ? `${selectedImageIndex + 1} / ${getImages(selectedBuild).length} images`
                    : "No images"}
                </div>
              </div>

              <button
                onClick={closeBuildViewer}
                className="shrink-0 p-3 rounded-full border border-white/10 hover:bg-white/10 transition"
              >
                <X size={20} />
              </button>
            </div>

            {/* Main image */}
            <div className="relative bg-black min-h-[55vh] sm:min-h-[65vh] flex items-center justify-center">
              {getImages(selectedBuild).length > 0 ? (
                <>
                  <img
                    src={getImages(selectedBuild)[selectedImageIndex]}
                    alt={`${selectedBuild.title || "Build"} ${selectedImageIndex + 1}`}
                    className="max-h-[70vh] max-w-full w-auto object-contain cursor-zoom-in"
                    onClick={() => setFullscreenImage(true)}
                  />

                  {getImages(selectedBuild).length > 1 && (
                    <>
                      <button
                        onClick={() => moveImage("prev")}
                        className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-black/70 border border-white/20 hover:bg-black/90 flex items-center justify-center transition"
                        aria-label="Previous image"
                      >
                        <ChevronLeft size={24} />
                      </button>

                      <button
                        onClick={() => moveImage("next")}
                        className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-black/70 border border-white/20 hover:bg-black/90 flex items-center justify-center transition"
                        aria-label="Next image"
                      >
                        <ChevronRight size={24} />
                      </button>
                    </>
                  )}

                  <button
                    onClick={() => setFullscreenImage(true)}
                    className="absolute right-4 bottom-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-black/75 border border-white/20 hover:bg-black transition text-sm"
                  >
                    <Maximize size={16} />
                    Full Screen
                  </button>

                  {/* Counter */}
                  {getImages(selectedBuild).length > 1 && (
                    <div className="absolute left-4 bottom-4 px-3 py-1.5 rounded-full bg-black/75 border border-white/10 text-xs">
                      {selectedImageIndex + 1} / {getImages(selectedBuild).length}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center text-white/30 py-32">
                  <ImageIcon size={56} />
                  <div className="mt-3">No images uploaded</div>
                </div>
              )}
            </div>

            {/* Thumbnail strip */}
            {getImages(selectedBuild).length > 1 && (
              <div className="px-4 sm:px-6 py-4 border-t border-white/10 bg-black/30">
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {getImages(selectedBuild).map((image, index) => (
                    <button
                      key={`${image}-${index}`}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`relative shrink-0 w-24 h-16 sm:w-32 sm:h-20 rounded-xl overflow-hidden border-2 transition ${
                        selectedImageIndex === index
                          ? "border-[#FFBF00]"
                          : "border-white/10 hover:border-white/40"
                      }`}
                    >
                      <img
                        src={image}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />

                      <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/80 text-[10px]">
                        {index + 1}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Details */}
            <div className="p-4 sm:p-6 border-t border-white/10">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/30">
                    Player
                  </div>
                  <div className="text-sm mt-1">
                    {selectedBuild.author_name ||
                      selectedBuild.author ||
                      "Unknown"}
                  </div>
                </div>

                <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/30">
                    Server
                  </div>
                  <div className="text-sm mt-1">
                    {selectedBuild.server || "Unknown"}
                  </div>
                </div>

                <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/30">
                    Category
                  </div>
                  <div className="text-sm mt-1">
                    {selectedBuild.category || "Spawn"}
                  </div>
                </div>

                <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/30">
                    Submitted
                  </div>
                  <div className="text-sm mt-1">
                    {formatDate(selectedBuild.created_at)}
                  </div>
                </div>
              </div>

              {selectedBuild.description && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">
                    Description
                  </div>

                  <p className="text-sm text-white/70 whitespace-pre-wrap leading-relaxed">
                    {selectedBuild.description}
                  </p>
                </div>
              )}

              {selectedBuild.coordinates && (
                <div className="flex items-center gap-2 mt-4 text-sm text-white/60">
                  <MapPin size={15} />
                  {selectedBuild.coordinates}
                </div>
              )}

              {/* Automatic verification result */}
              {autoResults[selectedBuild.id] && (
                <div
                  className={`mt-5 rounded-xl border px-4 py-4 text-sm ${
                    autoResults[selectedBuild.id].approved
                      ? "border-green-400/20 bg-green-500/10 text-green-300"
                      : "border-yellow-400/20 bg-yellow-500/5 text-yellow-200"
                  }`}
                >
                  <div className="font-semibold">
                    {autoResults[selectedBuild.id].approved
                      ? "✓ LosTDC SURVIVAL verified"
                      : "⚠ Manual review required"}
                  </div>

                  <div className="text-white/50 mt-1">
                    {autoResults[selectedBuild.id].reason}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
                    <div className="rounded-lg bg-black/20 px-3 py-2">
                      <div className="text-[10px] uppercase tracking-widest text-white/30">
                        OCR
                      </div>
                      <div className="text-xs text-white/70 mt-1 break-words">
                        {autoResults[selectedBuild.id].text ||
                          "Nothing detected"}
                      </div>
                    </div>

                    <div className="rounded-lg bg-black/20 px-3 py-2">
                      <div className="text-[10px] uppercase tracking-widest text-white/30">
                        Normal OCR
                      </div>
                      <div className="text-xs text-white/70 mt-1 break-words">
                        {autoResults[selectedBuild.id].normalText ||
                          "Nothing detected"}
                      </div>
                    </div>

                    <div className="rounded-lg bg-black/20 px-3 py-2">
                      <div className="text-[10px] uppercase tracking-widest text-white/30">
                        Yellow OCR
                      </div>
                      <div className="text-xs text-white/70 mt-1 break-words">
                        {autoResults[selectedBuild.id].yellowText ||
                          "Nothing detected"}
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 text-xs text-white/40">
                    Yellow pixel coverage:{" "}
                    {(
                      (autoResults[selectedBuild.id].yellowCoverage || 0) *
                      100
                    ).toFixed(2)}
                    %
                  </div>

                  {autoResults[selectedBuild.id].debugImage && (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-xs text-white/40 hover:text-white/70">
                        Show exactly what OCR checked
                      </summary>
                      <div className="mt-3 rounded-xl overflow-hidden border border-white/10 bg-black">
                        <img
                          src={autoResults[selectedBuild.id].debugImage}
                          alt="Scoreboard crop used for OCR"
                          className="w-full max-w-2xl max-h-72 object-contain"
                        />
                      </div>
                    </details>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-3 mt-6 pt-5 border-t border-white/10">
                <button
                  onClick={() => autoApproveBuild(selectedBuild)}
                  disabled={actionId === selectedBuild.id || autoChecking}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-yellow-400/30 text-yellow-200 text-sm font-semibold hover:bg-yellow-500/10 transition disabled:opacity-50"
                >
                  <Eye size={17} />
                  {autoChecking ? "Checking..." : "Auto Check"}
                </button>

                <button
                  onClick={() => approveBuild(selectedBuild)}
                  disabled={actionId === selectedBuild.id}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FFBF00] text-black text-sm font-semibold hover:bg-[#FFCF33] transition disabled:opacity-50"
                >
                  <Check size={17} />
                  Approve Build
                </button>

                <button
                  onClick={() => openReject(selectedBuild)}
                  disabled={actionId === selectedBuild.id}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-red-400/30 text-red-300 hover:bg-red-500/10 text-sm transition disabled:opacity-50"
                >
                  <X size={17} />
                  Reject Build
                </button>

                <button
                  onClick={closeBuildViewer}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/10 hover:bg-white/5 text-sm transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen image viewer */}
      {fullscreenImage &&
        selectedBuild &&
        getImages(selectedBuild).length > 0 && (
          <div
            className="fixed inset-0 z-[200] bg-black flex items-center justify-center"
            onClick={() => setFullscreenImage(false)}
          >
            <img
              src={getImages(selectedBuild)[selectedImageIndex]}
              alt={`${selectedBuild.title || "Build"} fullscreen`}
              className="max-w-[96vw] max-h-[94vh] object-contain"
            />

            {/* Close */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setFullscreenImage(false);
              }}
              className="absolute top-5 right-5 w-12 h-12 rounded-full bg-black/70 border border-white/20 hover:bg-black flex items-center justify-center"
            >
              <X size={22} />
            </button>

            {/* Previous */}
            {getImages(selectedBuild).length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    moveImage("prev");
                  }}
                  className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black/70 border border-white/20 hover:bg-black flex items-center justify-center"
                >
                  <ChevronLeft size={26} />
                </button>

                {/* Next */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    moveImage("next");
                  }}
                  className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black/70 border border-white/20 hover:bg-black flex items-center justify-center"
                >
                  <ChevronRight size={26} />
                </button>

                <div className="absolute bottom-5 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/80 border border-white/10 text-sm">
                  {selectedImageIndex + 1} / {getImages(selectedBuild).length}
                </div>
              </>
            )}
          </div>
        )}

      {/* Reject modal */}
      {rejectingBuild && (
        <div className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-2xl glass p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-red-300">
              Reject Build
            </div>

            <h2 className="font-hero text-2xl mt-1">
              {rejectingBuild.title}
            </h2>

            <p className="text-sm text-white/50 mt-2">
              Enter a reason. The player will receive this reason with the
              rejection notification.
            </p>

            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Example: Please upload clearer screenshots showing the complete build."
              rows={5}
              className="w-full mt-4 rounded-xl bg-black/50 border border-white/10 px-4 py-3 text-sm text-white outline-none focus:border-white/20"
              autoFocus
            />

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  setRejectingBuild(null);
                  setRejectReason("");
                }}
                className="px-4 py-2 rounded-full border border-white/10 text-sm"
              >
                Cancel
              </button>

              <button
                onClick={rejectBuild}
                disabled={
                  !rejectReason.trim() ||
                  actionId === rejectingBuild.id
                }
                className="px-4 py-2 rounded-full bg-red-500/80 text-white text-sm font-semibold disabled:opacity-50"
              >
                {actionId === rejectingBuild.id
                  ? "Rejecting..."
                  : "Reject Build"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}